import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecurityLogging } from './useSecurityLogging';

interface SecurityMetrics {
  failedAttempts: number;
  suspiciousActivity: boolean;
  lastFailedAttempt?: string;
  isRateLimited: boolean;
}

export const useSecurityMonitoring = () => {
  const [metrics, setMetrics] = useState<SecurityMetrics>({
    failedAttempts: 0,
    suspiciousActivity: false,
    isRateLimited: false
  });

  const { logSecurityEvent } = useSecurityLogging();

  const checkAuthRateLimit = async (email: string, ip?: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('check_enhanced_auth_rate_limit', {
        user_email: email,
        client_ip: ip
      });

      if (error) {
        console.error('Rate limit check failed:', error);
        return true; // Allow on error to prevent blocking
      }

      return data || false;
    } catch (error) {
      console.error('Rate limit check error:', error);
      return true;
    }
  };

  const logFailedAuth = async (
    email: string,
    attemptType: 'login' | 'otp' | 'token' = 'login',
    failureReason?: string,
    ip?: string,
    userAgent?: string
  ) => {
    try {
      await supabase.rpc('log_failed_auth_attempt', {
        p_email: email,
        p_ip_address: ip,
        p_user_agent: userAgent,
        p_attempt_type: attemptType,
        p_failure_reason: failureReason
      });

      // Update local metrics
      setMetrics(prev => ({
        ...prev,
        failedAttempts: prev.failedAttempts + 1,
        lastFailedAttempt: new Date().toISOString(),
        suspiciousActivity: prev.failedAttempts >= 2
      }));

      // Log to security audit
      await logSecurityEvent({
        action_type: 'failed_authentication',
        resource_type: 'auth',
        metadata: {
          email,
          attempt_type: attemptType,
          failure_reason: failureReason,
          ip_address: ip
        }
      });
    } catch (error) {
      console.error('Failed to log auth failure:', error);
    }
  };

  const detectSuspiciousActivity = (failedAttempts: number, timeWindow: number = 300000): boolean => {
    // Detect if there are more than 3 failed attempts in 5 minutes
    return failedAttempts > 3;
  };

  const clearMetrics = () => {
    setMetrics({
      failedAttempts: 0,
      suspiciousActivity: false,
      isRateLimited: false
    });
  };

  // Monitor for real-time security events — only when user is authenticated
  useEffect(() => {
    // Skip realtime subscription if no authenticated session to avoid
    // unnecessary WebSocket connections that cause console errors
    const setupChannel = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const channel = supabase
        .channel('security-monitoring')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'failed_auth_attempts'
          },
          (payload) => {
            console.log('New failed auth attempt detected:', payload);
            setMetrics(prev => ({
              ...prev,
              failedAttempts: prev.failedAttempts + 1,
              suspiciousActivity: detectSuspiciousActivity(prev.failedAttempts + 1)
            }));
          }
        )
        .subscribe();

      return channel;
    };

    let channelRef: ReturnType<typeof supabase.channel> | undefined;
    setupChannel().then(ch => { channelRef = ch; });

    return () => {
      if (channelRef) supabase.removeChannel(channelRef);
    };
  }, []);

  return {
    metrics,
    checkAuthRateLimit,
    logFailedAuth,
    detectSuspiciousActivity,
    clearMetrics
  };
};